/* iter-50k — One-click unsubscribe landing for help-update broadcasts.
 *
 * Linked from the footer of every The Loom help-update email. The
 * `token` query param is the per-user secret stored on the user row;
 * we POST it to /api/help/unsubscribe and the backend flips
 * `help_updates_opt_in: false`. No login required.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, XCircle, MailX } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function HelpUnsubscribePage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [state, setState] = useState({ phase: 'busy' });

  useEffect(() => {
    if (!token) {
      setState({ phase: 'error', detail: 'Missing token.' });
      return;
    }
    axios.post(`${API}/help/unsubscribe`, null, { params: { token } })
      .then((r) => setState({ phase: 'done', body: r.data }))
      .catch((e) => setState({
        phase: 'error',
        detail: e?.response?.data?.detail || e.message,
      }));
  }, [token]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10 text-foreground" data-testid="help-unsub-page">
      <div className="w-full max-w-md rounded-md border border-border bg-card p-6 shadow-panel-active">
        <div className="mb-3 flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
          <MailX className="h-3.5 w-3.5" /> Help-update broadcasts
        </div>
        {state.phase === 'busy' && (
          <div className="flex items-center gap-2 text-[12.5px] text-muted-foreground" data-testid="help-unsub-busy">
            <Loader2 className="h-4 w-4 animate-spin" /> Unsubscribing…
          </div>
        )}
        {state.phase === 'done' && (
          <div data-testid="help-unsub-done">
            <div className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="h-5 w-5" />
              <span className="text-[14px] font-semibold">You're unsubscribed.</span>
            </div>
            <div className="mt-2 text-[12px] text-muted-foreground">
              You will no longer receive The Loom help updates.
            </div>
            <div className="mt-2 text-[11.5px] text-muted-foreground">
              You can re-subscribe any time from your account settings.
            </div>
            <Link to="/" className="mt-5 inline-block text-[12px] text-primary hover:underline">Back to Crown & Rose →</Link>
          </div>
        )}
        {state.phase === 'error' && (
          <div data-testid="help-unsub-error">
            <div className="flex items-center gap-2 text-rose-700">
              <XCircle className="h-5 w-5" />
              <span className="text-[14px] font-semibold">We couldn't process that link.</span>
            </div>
            <div className="mt-2 text-[12px] text-muted-foreground">{state.detail || 'Try the link from the email again.'}</div>
          </div>
        )}
      </div>
    </div>
  );
}
